import { FieldConfig, useField } from 'formik';
import { isEmpty } from 'lodash';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  NumericInput,
  NumericInputProps,
  NumericInputRef,
} from '../../../../../components/inputs/numeric-input';
import { IonItem } from '../../../../../components/ionic/IonItem';
import { useWidgetClasses } from '../../hook/widget.hooks';
import { getNumericFormatSuffix } from '../../util/widget';
import { WidgetLabel } from './widget-label';
import {
  NumericFormat,
  NumericWidgetProps,
  WidgetRef,
  createWidgetExtension,
} from './widget.types';

interface NumericInputWidgetProps extends NumericWidgetProps {
  format?: NumericFormat;
  requireNegative?: boolean;
  triggerOn?: 'ionInput' | 'ionChange';
}

const NumericInputWidget = (
  props: NumericInputWidgetProps & FieldConfig
): ReactElement => {
  const {
    label,
    description,
    ionItemProps,
    extensions,
    disabled,
    showDescription,
    step,
    subtitle,
    subtitleColor,
    format,
    isNested,
    isPaywalled,
    transparentBackground,
    requireNegative,
    triggerOn = 'ionInput',
    ...otherProps
  } = props;
  const { className: ionItemClassName, ...restIonItemProps } =
    ionItemProps ?? {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [field, meta, helper] = useField(otherProps as any);
  const inputRef = useRef<NumericInputRef>(null);
  const [isDisabled, setIsDisabled] = useState(disabled ?? false);

  const rightElement = useMemo(() => {
    return createWidgetExtension(extensions?.rightElement, {
      field: [field, meta, helper],
      widgetRef: inputRef.current as WidgetRef,
      setDisabled: setIsDisabled,
      isDisabled,
    });
  }, [extensions?.rightElement, field, helper, isDisabled, meta]);

  useEffect(() => {
    if (disabled !== undefined) {
      setIsDisabled(disabled);
    }
  }, [disabled]);

  const classList = useWidgetClasses({
    isNested,
    transparentBackground,
    isPaywalled,
  });

  const ionLabelClass = isNested ? 'ion-padding-start' : undefined;

  const handleUpdate = useCallback(
    (e: CustomEvent) => {
      if (isPaywalled) {
        return;
      }

      // Handle empty string as null
      if (isEmpty(e.detail.value)) {
        helper.setValue(null);
        return;
      }
      helper.setValue(e.detail.value);
    },
    [helper, isPaywalled]
  );

  const inputProps = useMemo(() => {
    const baseProps: NumericInputProps['ionInputProps'] = {
      step: step ? `${step}` : undefined,
      disabled: isDisabled,
      value: field.value,
      'aria-label': label,
      'data-cy': otherProps.name,
    };

    if (triggerOn === 'ionInput') {
      return {
        ...baseProps,
        onIonInput: handleUpdate,
      };
    } else {
      return {
        ...baseProps,
        onIonChange: handleUpdate,
      };
    }
  }, [
    field.value,
    handleUpdate,
    isDisabled,
    label,
    otherProps.name,
    triggerOn,
    step,
  ]);

  return (
    <IonItem
      className={`${classList} ${ionItemClassName || ''}`.trim()}
      lines="full"
      disabled={isDisabled}
      onClick={async () => {
        const nativeInput = await inputRef.current?.getInputElement();
        nativeInput?.select();
      }}
      {...restIonItemProps}
      onTouchStart={(e) => {
        inputRef.current?.inputFocus(e);
      }}
      onTouchEnd={(e) => {
        inputRef.current?.inputFocus(e);
      }}
    >
      <NumericInput
        ref={inputRef}
        requireNegative={requireNegative}
        ionInputProps={inputProps}
      >
        <div slot="label">
          <WidgetLabel
            label={label}
            description={description}
            showDescription={showDescription}
            error={meta.error}
            forceDisable={isDisabled}
            subtitle={subtitle}
            subtitleColor={subtitleColor}
            ionLabelProps={{
              className: ionLabelClass,
            }}
          />
        </div>
        {rightElement}
      </NumericInput>
      {format && getNumericFormatSuffix(format)}
    </IonItem>
  );
};

export { NumericInputWidget };
